import { useEffect, useRef } from 'react';

/** The nearest ancestor that scrolls (the layout's <main>), or null for the viewport. */
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
  }
  return null;
};

/**
 * Calls `onLoadMore` when the returned sentinel ref scrolls within `margin` px of view — attach it
 * to an element after the list. Observes the list's own scroll container, so the margin preloads
 * the next page before the user reaches the bottom.
 *
 * `itemCount` re-arms the observer after each page: if the sentinel is still in view (a short page,
 * a tall screen) it fires again at once, until the list fills the view or `enabled` turns false.
 */
export function useInfiniteScroll<T extends HTMLElement>(
  onLoadMore: () => void,
  { enabled, itemCount, margin = 600 }: { enabled: boolean; itemCount: number; margin?: number },
) {
  const sentinelRef = useRef<T | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!enabled || !sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current();
      },
      { root: scrollParent(sentinel), rootMargin: `0px 0px ${margin}px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, itemCount, margin]);

  return sentinelRef;
}
